import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useAppDispatch } from 'store/hooks'
import { offeringUpdateThunk } from 'store/reducers/temple/offeringSlice'
import { IOfferingResponse } from 'types/api/temple'
import { extractError } from 'utils/api'

type BulkOfferingMediumState = {
    availableOnline: boolean
    availableCounter: boolean
    availableKiosk: boolean
    availablePos: boolean
}

interface BulkOfferingMediumDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const getInitialState = (offering?: IOfferingResponse): BulkOfferingMediumState => ({
    availableOnline: offering?.availableOnline ?? false,
    availableCounter: offering?.availableCounter ?? false,
    availableKiosk: offering?.availableKiosk ?? false,
    availablePos: offering?.availablePos ?? false
})

const BulkOfferingMediumDialog: React.FC<BulkOfferingMediumDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [values, setValues] = useState<BulkOfferingMediumState>(getInitialState())
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (open) {
            setValues(getInitialState(offerings[0]))
        }
    }, [open, offerings])

    const handleChange = (key: keyof BulkOfferingMediumState) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setValues(prev => ({ ...prev, [key]: event.target.checked }))
    }

    const handleSave = async () => {
        if (offerings.length === 0) return

        setSubmitting(true)
        try {
            await Promise.all(
                offerings.map(offering =>
                    dispatch(offeringUpdateThunk({
                        url: offering.url,
                        data: values
                    })).then(unwrapResult)
                )
            )
            enqueueSnackbar('Updated selected offerings', { variant: 'success' })
            onComplete?.()
            onClose()
        } catch (error) {
            enqueueSnackbar(extractError(error) ?? 'Failed to update selected offerings', { variant: 'error' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Bulk Offering Medium</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Apply these availability flags to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}.
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                    <FormControlLabel
                        control={<Switch checked={values.availableOnline} onChange={handleChange('availableOnline')} />}
                        label="App/Web"
                    />
                    <FormControlLabel
                        control={<Switch checked={values.availableCounter} onChange={handleChange('availableCounter')} />}
                        label="Counter"
                    />
                    <FormControlLabel
                        control={<Switch checked={values.availablePos} onChange={handleChange('availablePos')} />}
                        label="POS"
                    />
                    <FormControlLabel
                        control={<Switch checked={values.availableKiosk} onChange={handleChange('availableKiosk')} />}
                        label="Kiosk"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="outlined" disabled={submitting}>
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={submitting || offerings.length === 0}>
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BulkOfferingMediumDialog
